import { shallowRef, watch, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCustomToast, type Breadcrumb } from '@/shared/lib';
import { MATRIX_NOT_FOUND_TOAST_TITLE } from '../config';
import {
  buildFolderBreadcrumbTrail,
  getContainerMatrices,
  isCustomFolderCode,
} from './mapVisibleTreeToNavigation';
import { findVisibleFolderByCode, loadVisibleDirectoryTree } from './useVisibleDirectoryTree';
import type { VisibleFolderEntry } from './visible-tree.types';

export const MODULE_MATRIX_REVISION_ROUTE_NAME = 'ModuleMatrixRevision';

// The tree labels a matrix item `RQ50.10`, while the matrix and generated record routes take the
// stored prefix `50.10`, which never holds the label.
const MATRIX_PREFIX_LABEL = /^RQ/;

export interface MatrixRevisionScope {
  folderId: number;
  prefix: string;
  matrixId: number;
  title: string;
  breadcrumbTrail: Breadcrumb[];
}

export function resolveMatrixRevisionScope(
  entry: VisibleFolderEntry | null,
  currentPath: string
): MatrixRevisionScope | null {
  if (!entry || entry.moduleId === null) return null;

  const { folder } = entry;
  if (folder.contentKind !== 'MATRIX_REVISION') return null;

  const matrix = getContainerMatrices(folder)[0];
  const matrixId = folder.matrixId ?? matrix?.itemId;
  const prefix = matrix?.prefix?.replace(MATRIX_PREFIX_LABEL, '');
  if (!prefix || !matrixId) return null;

  return {
    folderId: folder.id,
    prefix,
    matrixId,
    title: folder.name,
    breadcrumbTrail: buildFolderBreadcrumbTrail(entry, currentPath),
  };
}

export function useModuleMatrixRevisionScope(folderCode: Ref<string>) {
  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();

  const scope = shallowRef<MatrixRevisionScope | null>(null);
  let latestRequest = 0;

  async function resolve(): Promise<void> {
    const request = ++latestRequest;
    const code = folderCode.value;
    scope.value = null;

    let resolved: MatrixRevisionScope | null = null;
    if (isCustomFolderCode(code)) {
      try {
        await loadVisibleDirectoryTree();
        resolved = resolveMatrixRevisionScope(findVisibleFolderByCode(code), route.path);
      } catch {
        resolved = null;
      }
    }

    if (request !== latestRequest) return;

    if (resolved) {
      scope.value = resolved;
      return;
    }

    showErrorToast({ title: MATRIX_NOT_FOUND_TOAST_TITLE, type: 'error' });
    await router.replace({ name: 'Home' });
  }

  watch(folderCode, resolve, { immediate: true });

  return { scope };
}
